import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import { handleAPIError, ERROR_TYPE } from '@/utils/errorHelper';
import { redirectToApp, redirectToAppWithError, filterAccounts } from '@/utils/appLoginHelper';
import { fixProtocol, navigateExternalURL, buildQueryString } from '@/utils/urlHelper';
import { smartFocus } from '@/utils/uiHelper';
import GDPRConsent from '@/components/sections/GDPRConsent/GDPRConsent.vue';
import AppConsent from '@/components/sections/AppConsent/AppConsent.vue';
import AccountList from '@/components/widgets/AccountList/AccountList.vue';
import PageHeader from '@/components/widgets/PageHeader/PageHeader.vue';
import CompanyInfo from '@/components/sections/Login/CompanyInfo.vue';
import Loading from '@/components/widgets/Loading/Loading.vue';
import TfaSetup from '@/components/sections/Login/TfaSetup.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import PersonaCapture from '@/components/sections/PersonaCapture/PersonaCapture.vue';

// @vue/component
export default {
  name: 'Login',
  components: {
    'gdpr-consent': GDPRConsent,
    AppConsent,
    CompanyInfo,
    AccountList,
    PageHeader,
    Loading,
    TfaSetup,
    PasswordInput,
    PersonaCapture,
  },
  data() {
    return {
      rememberMe: false,
      twoFactorPasscode: '',
      gdprSeen: false,
      companyInfo: undefined,

      passwordEncrypted: false, // for legacy form support

      code: '',
      accounts: [],
      selectedAccount: '',

      state: 'normal',
      formError: '',
      isLoading: false,

      email: undefined,
      password: undefined,
      forced: false,
    };
  },
  created() {
    // Get query params submitted via legacy forms
    if (this.$route.query.email) this.email = this.$route.query.email;
    if (this.$route.query.password) this.password = this.$route.query.password;
    if (this.$route.query.automaticSubmit) this.automaticSubmit = true;
    if (this.$route.query.product) this.product = this.$route.query.product;
    this.passwordEncrypted = !!this.$route.query.encrypted;

    // hide legacy params
    const newParams = Object.assign({}, this.$route.query);
    delete newParams.email;
    delete newParams.password;
    delete newParams.automaticSubmit;
    delete newParams.encrypted;
    delete newParams.product;
    this.$router.replace({ query: newParams });

    if (this.automaticSubmit) {
      this.$nextTick(function () { // eslint-disable-line func-names
        this.onSubmit();
      });
    }
  },
  mounted() {
    window.parent.postMessage('pageResized', '*');
    if (this.loggedIn) this.continueToProduct();
    smartFocus();
  },
  computed: {
    ...mapGetters({
      samlURL: 'samlURL',
      googleSSOURL: 'googleSSOURL',
      allowStandardLogin: 'allowStandardLogin',
    }),
    ...mapState({
      loginType: (state) => state.login.loginType,
      appInfo: (state) => state.login.appInfo,
      meta: (state) => state.login.meta,
      loggedIn: (state) => state.login.loggedIn,
      ignoreInstallations: (state) => state.login.meta.ignoreInstallations,
      /* eslint-disable */
      afterLoginURL: (state) => {
        const baseURL = state.login.loginType === 'account' ? window.location.href.replace(/(\/launchpad\/login)\/?/, '/') : '';
        return state.login.afterLoginURL || baseURL;
      },
      showRememberMe: (state) => state.site.siteInfo.displayOptions.allowRememberMe || state.login.loginType === 'generic',
      showGoogleSSO: (state) => state.site.siteInfo.integrations.googleSignIn.enabled || state.login.loginType !== 'account',
      showSAML: (state) => state.site.siteInfo.sso.enabled || state.login.loginType !== 'account',
      startText: (state) => state.site.siteInfo.displayOptions.startText || '',
      title: (state) => state.site.siteInfo.displayOptions.showSiteNameOnLoginScreen ? state.site.siteInfo.name : 'Log in',
      isEnterprise: (state) => state.site.siteInfo.isEnterprise,
      inAppLogin: (state) => state.login.loginType === 'app',
      isValidationPending: (state) => state.site.siteInfo.installationState === 'validation-pending',
      /* eslint-enable */
    }),
    forgotPasswordLink() {
      let url = '/forgotpassword';
      if (this.$route.params.productCode) url += `/${this.$route.params.productCode}`;
      return url;
    },
  },
  methods: {
    onSubmit() {
      this.isLoading = true;
      this.formError = '';

      const data = {
        email: this.email,
        password: this.password,
        rememberMe: this.rememberMe,
        product: this.product,
      };

      // For app login flow, we want to allow apps to hide some accounts,
      // for example if they are already authenticated.
      if (this.inAppLogin && this.ignoreInstallations) {
        data.ignoreInstallations = this.ignoreInstallations;
      }

      if (this.gdprSeen) data.gdprSeen = true;
      if (this.inAppLogin) {
        data.redirect_uri = this.appInfo.redirect_uri;
        data.state = this.appInfo.state;
        data.client_id = this.appInfo.client_id;
        data.scope = this.appInfo.scope;
        data.install = this.appInfo.install;
        data.nonce = this.appInfo.nonce;
      }
      if (this.passwordEncrypted) data.passwordEncrypted = true;

      if (this.loginType === 'account') {
        this.submitLogin('', data);
      } else if (this.selectedAccount) {
        this.onAccountSelected(this.selectedAccount);
      } else if (this.password === 'xxx' && this.email.indexOf('@') === -1) {
        // logging in via API old key on generic login
        this.submitLogin('', data);
      } else if (this.loginType === 'app' && !this.meta.generic) {
        this.submitLogin('', data);
      } else {
        this.submitGetAccounts(data);
      }
    },
    initSAML() {
      if (this.loginType === 'account') {
        navigateExternalURL(this.samlURL, this.$store.state.login.meta.isIframe);
      } else {
        this.$store.commit('setLoginState', 'saml-email');
      }
    },
    submitLogin(baseURL, data) {
      let url = `${baseURL}/launchpad/v1/login.json`;
      if (this.loginType !== 'account') url += '?appAuth=true';

      /* eslint-disable */
      if (this.twoFactorPasscode) data.twoFactorPasscode = this.twoFactorPasscode;
      if (this.gdprSeen) data.gdprSeen = this.gdprSeen;
      if (this.inAppLogin) {
        data.redirect_uri = this.appInfo.redirect_uri;
        data.state = this.appInfo.state;
        data.client_id = this.appInfo.client_id;
        data.scope = this.appInfo.scope;
        data.install = this.appInfo.install;
        data.nonce = this.appInfo.nonce;
      }
      /* eslint-enable */
      const loginTypeTemp = this.loginType;

      axios.post(url, data).then((response) => {
        this.code = response.data.code;

        if (response.data.setupScreenRequired) {
          const { industryCategoryId, companySizeId } = response.data;

          this.companyInfo = {
            industryCategoryId: industryCategoryId || undefined,
            sizeId: companySizeId || undefined,
          };

          this.state = 'company-info';
          this.isLoading = false;

          return;
        }

        if (!response.data.gdprSeen) {
          this.state = 'gdpr-consent';
          this.gdprSeen = true;
          this.isLoading = false;
          return;
        }

        if (response.data.userPersona !== undefined && !response.data.userPersona && loginTypeTemp !== 'generic') {
          this.state = 'persona-capture';
          this.$store.commit('setFormState', this.state);
          this.isLoading = false;
          return;
        }

        switch (this.loginType) { // eslint-disable-line
          case 'app':
            this.app = response.data.app;
            if (this.app.consentRequired === false) { // allow skipping screen for our own apps
              this.state = 'app-success';
              axios.post(`${baseURL}/launchpad/v1/token/consent.json`, { code: this.code }).then((resp) => {
                redirectToApp(this.appInfo, resp.data.code);
              }).catch(() => {
                redirectToAppWithError(this.appInfo, 'failed_to_consent', 'impossible to consent');
              });
            } else {
              this.state = 'app-consent';
            }
            this.isLoading = false;
            return;
          case 'generic': // eslint-disable-line
            // baseURL in this case is account URL without trailing slash
            let continueURL = '';
            if (this.$route.params.productCode) continueURL += `${baseURL}/${this.$route.params.productCode}`;

            const params = {
              code: this.code,
              continue: this.afterLoginURL || data.continue || continueURL,
            };

            window.location.href = fixProtocol(`${baseURL}/launchpad/tokenauth?${buildQueryString(params)}`);
            return;
        }

        this.$store.commit('setLoggedIn', {
          loggedIn: true,
          userId: response.data.userId,
        });

        // eslint-disable-next-line
        const continueURL = this.afterLoginURL || data.continue || window.location.href.replace(/(\/launchpad\/login)\/?/, '/');
        window.location.href = continueURL;
      }).catch((e) => {
        const err = handleAPIError(
          this.$store, e,
          'Please make sure you have the correct email or password.',
        );

        if (err && err.type === ERROR_TYPE.FORM_ERROR) this.formError = err.err;
        if (err && err.type === ERROR_TYPE.TFA_REQUIRED) this.state = '2fa';
        if (err && err.type === ERROR_TYPE.TFA_SETUP_REQUIRED) {
          this.state = '2fa-setup';
          this.forced = true;
        }
        if (err && err.type === ERROR_TYPE.INSTALLATION_VALIDATION_PENDING) {
          const isVerifyEmail = this.$route.query.continue && this.$route.query.continue.includes('verifyemail');

          if (isVerifyEmail) {
            window.location.href = fixProtocol(
              `${baseURL}${this.$route.query.continue}?auth=${e.response.data.unvalidatedAccountLoginCode}&email=${encodeURIComponent(this.email)}`,
            );
          } else {
            window.location.href = fixProtocol(
              `${baseURL}/launchpad/verifyemail/${
                this.$route.params.productCode ? this.$route.params.productCode : 'projects'
              }/firstsend?auth=${e.response.data.unvalidatedAccountLoginCode}&email=${encodeURIComponent(this.email)}`,
            );
          }
        }

        this.isLoading = false;
        this.passwordEncrypted = false;
        if (this.state === 'select-account') this.state = 'normal';
        smartFocus();
      });
    },
    submitGetAccounts(data) {
      this.$store.dispatch('getWafToken')
      .then((token) => {
        const headers = { 'Launchpad-Version': process.env.VERSION };
        if (token !== null) headers['x-aws-waf-token'] = token;
        return axios.post('/launchpad/v1/accounts.json?generic=true', data, { headers });
      })
      .then((response) => {
        // filter out accounts where provided product is disabled
        // note: we will do this on the backend after global data
        // for all products
        const accs = filterAccounts(response.data.accounts, this.meta.product);

        // If no accounts found, we either have wrong details or ignoring all accounts
        if (accs.length === 0) {
          this.formError = 'Please make sure you have the correct email or password.';
          if (response.data.ignoredAccounts === data.ignoreInstallations) {
            this.formError = 'You have already authenticated with all available Teamwork accounts.';
          }
          this.isLoading = false;
          smartFocus();
          return;
        }

        // If we only have one account, skip selection step
        if (accs.length === 1) {
          this.onAccountSelected(accs[0]);
          return;
        }

        this.accounts = accs;
        this.state = 'select-account';
        this.isLoading = false;
        smartFocus();
      }).catch((e) => {
        const err = handleAPIError(
          this.$store, e,
          'Please make sure you have the correct email or password.',
        );
        if (err.type === ERROR_TYPE.FORM_ERROR) this.formError = err.err;
        this.isLoading = false;
        smartFocus();
      });
    },
    onAccountSelected(account) {
      let url = account.installation.apiEndPoint;
      // eslint-disable-next-line
      if (process.env.NODE_ENV === 'development' || window.location.href.indexOf('sunbeam.teamwork.localhost') > -1) {
        url = account.installation.url.replace('https://', 'http://');
      }

      // remove trailing slash
      if (url[url.length - 1] === '/') url = url.substr(0, url.length - 1);

      this.selectedAccount = account;
      this.$store.commit('setSelectedAccount', account);

      this.submitLogin(url, {
        email: this.email,
        password: this.password,
        rememberMe: this.rememberMe,
      });
    },
    onClickGoogleSSO(e) {
      this.isLoading = true;
      const isIframe = this.$store.state.login.meta.isIframe;
      if (isIframe) e.preventDefault();
      navigateExternalURL(this.googleSSOURL, isIframe);
    },
    reset() {
      this.formError = '';
      this.state = 'normal';
      this.twoFactorPasscode = '';
      this.selectedAccount = null;
      this.isLoading = false;
      smartFocus();
    },
    continueToProduct() {
      // eslint-disable-next-line
      if (this.loginType !== 'account' || !this.loggedIn || !this.$route.params.productCode || this.isValidationPending) return;
      this.isLoading = true;
      window.location.href = this.afterLoginURL;
    },
  },
  watch: {
    isLoading() {
      window.parent.postMessage('pageResized', '*');
    },
    loggedIn(loggedIn) {
      if (loggedIn) this.continueToProduct();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/sections/Login/StandardForm/StandardForm.js